import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { api } from "../lib/api";
import { LINKS } from "../lib/constants";

const TEAM_IMG = "/team.jpg";

const interests = ["Partnership", "Sponsorship", "Co-Programming", "Resource Sharing", "Other"];

export default function GetInvolved() {
  const [form, setForm] = useState({ name: "", organization: "", email: "", interest: "Partnership", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/inquiries", form);
      setDone(true);
      toast.success("Thanks — we'll be in touch soon.");
      setForm({ name: "", organization: "", email: "", interest: "Partnership", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-ink/20 py-3 text-ink text-base focus:outline-none focus:border-trailride focus:ring-0 transition-colors placeholder:text-ink/40";

  return (
    <div className="bg-paper min-h-screen" data-testid="get-involved-page">
      <Navbar />

      <header className="max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-44 pb-16 md:pb-24">
        <h1 className="font-serif text-5xl md:text-7xl font-black tracking-tight text-ink leading-[0.98] max-w-4xl">
          There's a place for you in the ride.
        </h1>
        <p className="font-sans text-lg md:text-xl text-ink/70 max-w-2xl mt-8 leading-relaxed">
          Whether you want to dance, serve, lead, or collaborate, Tiger Trailride creates ways to show
          up with purpose.
        </p>
      </header>

      {/* Section 1: Attend */}
      <section className="border-t border-ink/10" data-testid="section-attend">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-3">
            <span className="font-sans text-sm tracking-[0.24em] uppercase text-trailride font-semibold">01</span>
          </div>
          <div className="md:col-span-9 max-w-2xl">
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-ink mb-5">Attend an event</h2>
            <p className="font-sans text-ink/70 text-lg leading-relaxed mb-8">
              Come to line dancing classes, service events, and campus programs. No experience needed —
              just show up and move with us. It's the easiest way to feel the community.
            </p>
            <Link
              to="/events"
              data-testid="attend-view-events"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-trailride text-white font-sans font-medium hover:bg-trailride-dark hover:-translate-y-0.5 transition-all duration-300"
            >
              View Events <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Section 2: Exec Board */}
      <section className="border-t border-ink/10 bg-ink text-paper" data-testid="section-exec">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-3">
            <span className="font-sans text-sm tracking-[0.24em] uppercase font-semibold" style={{ color: "#9a6bbd" }}>02</span>
          </div>
          <div className="md:col-span-9 max-w-2xl">
            <p className="font-sans text-xs tracking-[0.2em] uppercase text-paper/50 mb-4">Application-based leadership</p>
            <h2 className="font-serif text-3xl md:text-5xl font-bold mb-5">Join the Executive Board</h2>
            <p className="font-sans text-paper/70 text-lg leading-relaxed mb-8">
              Build culture, create opportunities, and help lead Soul &amp; Service at LSU. Executive
              Board roles are selected through an application process each term.
            </p>
            <a
              href={LINKS.execBoardForm}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="exec-apply"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-paper text-ink font-sans font-medium hover:-translate-y-0.5 transition-all duration-300"
            >
              Apply for Executive Board <ArrowUpRight size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* Section 3: Partner + form */}
      <section className="border-t border-ink/10" data-testid="section-partner">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid md:grid-cols-12 gap-8 md:gap-12 items-start">
          <div className="md:col-span-5">
            <span className="font-sans text-sm tracking-[0.24em] uppercase text-trailride font-semibold">03</span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-ink mt-4 mb-5">Partner with us</h2>
            <p className="font-sans text-ink/70 text-lg leading-relaxed mb-6">
              For LSU departments, student organizations, and Baton Rouge businesses. We collaborate on
              partnership, sponsorship, co-programming, and resource-sharing that moves the community
              forward.
            </p>
            <div className="aspect-[4/3] overflow-hidden mt-8">
              <img src={TEAM_IMG} alt="Tiger Trailride team" className="w-full h-full object-cover grayscale-img" />
            </div>
          </div>

          <div className="md:col-span-7">
            {done ? (
              <div className="border border-ink/10 p-10 flex flex-col items-start gap-4" data-testid="partner-success">
                <CheckCircle2 size={40} className="text-trailride" />
                <h3 className="font-serif text-2xl font-bold text-ink">Message sent.</h3>
                <p className="font-sans text-ink/70">
                  Thanks for reaching out. A member of our team will get back to you soon.
                </p>
                <button
                  onClick={() => setDone(false)}
                  className="font-sans text-trailride font-medium hover:underline underline-offset-4"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-6" data-testid="partner-form">
                <h3 className="font-serif text-2xl font-bold text-ink mb-2">Start a partnership</h3>
                <div>
                  <label htmlFor="name" className="font-sans text-sm text-ink/60">Name</label>
                  <input id="name" data-testid="partner-name" required value={form.name} onChange={update("name")} className={inputCls} placeholder="Your name" />
                </div>
                <div>
                  <label htmlFor="organization" className="font-sans text-sm text-ink/60">Organization</label>
                  <input id="organization" data-testid="partner-org" value={form.organization} onChange={update("organization")} className={inputCls} placeholder="Company, department, or org" />
                </div>
                <div>
                  <label htmlFor="email" className="font-sans text-sm text-ink/60">Email</label>
                  <input id="email" type="email" data-testid="partner-email" required value={form.email} onChange={update("email")} className={inputCls} placeholder="you@email.com" />
                </div>
                <div>
                  <label htmlFor="interest" className="font-sans text-sm text-ink/60">Partnership interest</label>
                  <select id="interest" data-testid="partner-interest" value={form.interest} onChange={update("interest")} className={inputCls}>
                    {interests.map((i) => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="font-sans text-sm text-ink/60">Message</label>
                  <textarea id="message" data-testid="partner-message" required rows={4} value={form.message} onChange={update("message")} className={inputCls} placeholder="Tell us what you have in mind" />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="partner-submit"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-trailride text-white font-sans font-medium hover:bg-trailride-dark hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Start a Partnership"} <ArrowRight size={18} />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Current members */}
      <section className="border-t border-ink/10 bg-ink/[0.02]" data-testid="current-members-block">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="font-sans text-xs tracking-[0.24em] uppercase text-ink/40 mb-1">Current members</p>
            <p className="font-sans text-ink/70">Already accepted into Tiger Trailride? Access your dashboard.</p>
          </div>
          <Link
            to="/member-login"
            data-testid="current-members-login"
            className="inline-flex items-center gap-1.5 font-sans font-medium text-trailride hover:gap-2.5 transition-all"
          >
            Member Login <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
